import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import "./homeHero.css";

import h1 from "../../../assets/home/sports.webp";
import h2 from "../../../assets/home/agriculture.webp";
import h3 from "../../../assets/home/adas.webp";
import h4 from "../../../assets/home/aerial.webp";
import h5 from "../../../assets/home/robotics.webp";

const industries = [
  {
    name: "ADAS",
    image: h3,
    detail: "Autonomous Mobility",
    scanner: "OBJECT DETECTION",
    data: [
      { label: "VEHICLE", value: "TRACKING", position: "player" },
      { label: "OBJECT", value: "DETECTION", position: "field" },
      { label: "ROAD", value: "ANALYSIS", position: "motion" },
    ],
  },
  {
    name: "Aerial & Geospatial",
    image: h4,
    detail: "Geospatial Intelligence",
    scanner: "TERRAIN MAPPING",
    data: [
      { label: "TERRAIN", value: "MAPPING", position: "player" },
      { label: "LAND", value: "SEGMENTATION", position: "field" },
      { label: "AERIAL", value: "IMAGING", position: "motion" },
    ],
  },
  {
    name: "Agri-Tech",
    image: h2,
    detail: "Precision Agriculture",
    scanner: "CROP MONITORING",
    data: [
      { label: "CROP", value: "MONITORING", position: "player" },
      { label: "FIELD", value: "MAPPING", position: "field" },
      { label: "TERRAIN", value: "ANALYSIS", position: "motion" },
    ],
  },
  {
    name: "Robotics and Humanoids",
    image: h5,
    detail: "Robotics & Humanoids",
    scanner: "ROBOTIC MOTION",
    data: [
      { label: "ROBOT", value: "POSE ESTIMATION", position: "player" },
      { label: "HUMAN", value: "MOTION CAPTURE", position: "field" },
      { label: "OBJECT", value: "INTERACTION", position: "motion" },
    ],
  },
  {
    name: "Sports",
    image: h1,
    detail: "Sports Analytics",
    scanner: "PLAYER TRACKING",
    data: [
      { label: "PLAYER", value: "TRACKING", position: "player" },
      { label: "FIELD", value: "DETECTION", position: "field" },
      { label: "MOTION", value: "ANALYSIS", position: "motion" },
    ],
  },
];

const metrics = [
  { value: 20, suffix: "+", label: "INDUSTRIES SERVED" },
  { value: 950, suffix: "M+", label: "ASSETS ANNOTATED" },
  { value: 97, suffix: "%+", label: "QUALITY GUARANTEED" },
  { value: 100, suffix: "%", label: "IN-HOUSE DELIVERY" },
  { value: 24, suffix: "×7×365", label: "SECURE OPERATIONS" },
];

function useMagnetic() {
  const ref = useRef(null);
  

  const handlePointerMove = (event) => {
    if (event.pointerType !== "mouse") return;

    const element = ref.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;

    element.style.setProperty("--mag-x", `${x * 0.13}px`);
    element.style.setProperty("--mag-y", `${y * 0.13}px`);
  };

  const reset = () => {
    if (!ref.current) return;
    ref.current.style.setProperty("--mag-x", "0px");
    ref.current.style.setProperty("--mag-y", "0px");
  };

  return {
    ref,
    onPointerMove: handlePointerMove,
    onPointerLeave: reset,
    onBlur: reset,
  };
}

function MagneticLink({ to, children, className }) {
  const magnetic = useMagnetic();

  return (
    <Link
      to={to}
      className={className}
      ref={magnetic.ref}
      onPointerMove={magnetic.onPointerMove}
      onPointerLeave={magnetic.onPointerLeave}
      onBlur={magnetic.onBlur}
    >
      {children}
    </Link>
  );
}

function IndustryButton({ industry, index, activeIndex, onSelect }) {
  const magnetic = useMagnetic();
  const isActive = activeIndex === index;

  return (
    <button
      ref={magnetic.ref}
      type="button"
      className={`home-hero__thumbnail ${isActive ? "is-active" : ""}`}
      onClick={() => onSelect(index)}
      onPointerMove={magnetic.onPointerMove}
      onPointerLeave={magnetic.onPointerLeave}
      onBlur={magnetic.onBlur}
      aria-label={`Show ${industry.name} image`}
      aria-pressed={isActive}
    >
      <span className="home-hero__thumbnail-image">
        <img src={industry.image} alt="" loading="lazy" decoding="async" />
      </span>
      <span className="home-hero__thumbnail-name">{industry.name}</span>
      <span className="home-hero__thumbnail-progress">
        {isActive && <span key={index} />}
      </span>
    </button>
  );
}

export default function HomeHero() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const heroRef = useRef(null);
  const scannerRef = useRef(null);
  const metricRefs = useRef([]);
  const metricsRef = useRef(null);
  const countersStarted = useRef(false);

  useLayoutEffect(() => {
  const hero = heroRef.current;
  if (!hero) return undefined;

  const mm = gsap.matchMedia();

  mm.add("(prefers-reduced-motion: no-preference)", () => {
    gsap
      .timeline({ defaults: { ease: "power1.out" }, delay: 0.15 })
      .from(".home-hero__title", {
        autoAlpha: 0,
        duration: 0.8,
        clearProps: "opacity,visibility",
      })
      .from(
        ".home-hero__description",
        {
          autoAlpha: 0,
          duration: 0.8,
          clearProps: "opacity,visibility",
        },
        "-=0.35"
      );
  }, hero);

  return () => mm.revert();
}, []);

  // The HTML holds the real numbers (what crawlers and no-JS visitors read).
  // Visitors who get the count-up animation start from 0 instead.
  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    metricRefs.current.filter(Boolean).forEach((element) => {
      element.textContent = `0${element.dataset.suffix}`;
    });
  }, []);

  useEffect(() => {
    if (isFocused) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % industries.length);
    }, 2500);

    return () => window.clearInterval(timer);
  }, [activeIndex, isFocused]);

  useEffect(() => {
    const section = metricsRef.current;
    if (!section) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let startTimer;
    let animationFrame;

    const animateCounters = () => {
      if (countersStarted.current) return;
      countersStarted.current = true;

      const counters = metricRefs.current.filter(Boolean);

      if (reduceMotion) {
        counters.forEach((element) => {
          element.textContent = `${element.dataset.target}${element.dataset.suffix}`;
        });
        return;
      }

      const duration = 1800;
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);

        counters.forEach((element) => {
          const target = Number(element.dataset.target);
          const current = Math.floor(target * eased);
          element.textContent = `${current}${element.dataset.suffix}`;
        });

        if (progress < 1) {
          animationFrame = requestAnimationFrame(tick);
        }
      };

      animationFrame = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !countersStarted.current) {
          observer.disconnect();
          startTimer = window.setTimeout(animateCounters, 450);
        }
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -15% 0px",
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
      window.clearTimeout(startTimer);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  const handleScannerMove = (event) => {
    if (event.pointerType !== "mouse") return;

    const hero = heroRef.current;
    const scanner = scannerRef.current;
    if (!hero || !scanner) return;

    const rect = hero.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    scanner.style.left = `${x}px`;
    scanner.style.top = `${y}px`;
    scanner.style.opacity = "1";
  };

  const hideScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.style.opacity = "0";
    }
  };

  const selectIndustry = (index) => {
    setActiveIndex(index);
  };

  const activeIndustry = industries[activeIndex];

  return (
    <>
      <section
        ref={heroRef}
        className="home-hero"
        onPointerMove={handleScannerMove}
        onPointerLeave={hideScanner}
        onFocusCapture={() => setIsFocused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsFocused(false);
          }
        }}
        aria-label="Qualitas Global introduction"
      >
        <div className="home-hero__visual" aria-hidden="true">
          {industries.map((industry, index) => (
            <div
              key={industry.name}
              className={`home-hero__slide ${
                activeIndex === index ? "is-active" : ""
              }`}
              style={{ backgroundImage: `url("${industry.image}")` }}
            />
          ))}
        </div>

        <div className="home-hero__shade" aria-hidden="true" />
        <div className="home-hero__grid" aria-hidden="true" />

        <div className="home-hero__data" aria-hidden="true">
          {activeIndustry.data.map((item) => (
            <div
              key={item.label}
              className={`home-hero__data-tag home-hero__data-tag--${item.position}`}
            >
              <span className="home-hero__data-dot" />
              <span className="home-hero__data-copy">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </span>
            </div>
          ))}
        </div>

        <div className="home-hero__scanner" ref={scannerRef} aria-hidden="true">
          <span className="home-hero__scanner-ring" />
          <span className="home-hero__scanner-crosshair" />
          <span className="home-hero__scanner-label">
            <i />
            SCAN / {activeIndustry.scanner}
          </span>
        </div>

        <div className="home-hero__content">
          <h1 className="home-hero__title">
            Quality <em>Data</em> In.
            <br />
            Quality <em>AI</em> Out.
          </h1>

          <h2 className="home-hero__subheading">
            Quality Data. Reliable AI. Global Scale.
          </h2>

          <p className="home-hero__description">
            Human-in-the-loop annotation, collection, and evaluation, engineered
            to support your AI projects with high-quality data that improves
            accuracy and cuts time to production.
          </p>

          <div className="home-hero__actions">
            <MagneticLink to="/contact-us" className="home-hero__primary">
              <span>Request a free demo</span>
              <ArrowUpRight size={18} strokeWidth={1.8} />
            </MagneticLink>

            <MagneticLink to="/ai-infrastructure" className="home-hero__secondary">
              <span>Explore Our Services</span>
              <ArrowUpRight size={16} strokeWidth={1.7} />
            </MagneticLink>
          </div>
        </div>

        <div className="home-hero__controls">
          <div className="home-hero__active-label" aria-live="polite">
            <span className="home-hero__active-dot" />
            <span>{activeIndustry.detail}</span>
          </div>

          <div className="home-hero__thumbnails" aria-label="Choose industry">
            {industries.map((industry, index) => (
              <IndustryButton
                key={industry.name}
                industry={industry}
                index={index}
                activeIndex={activeIndex}
                onSelect={selectIndustry}
              />
            ))}
          </div>
        </div>

        <div className="home-hero__scroll" aria-hidden="true">
          <span>SCROLL TO EXPLORE</span>
          <i />
        </div>
      </section>

      <section
        className="home-metrics"
        ref={metricsRef}
        aria-label="Qualitas Global at a glance"
      >
        <div className="home-metrics__inner">
          {metrics.map((metric, index) => (
            <div className="home-metrics__item" key={metric.label}>
              <div
                className="home-metrics__value"
                ref={(element) => {
                  metricRefs.current[index] = element;
                }}
                data-target={metric.value}
                data-suffix={metric.suffix}
                aria-label={`${metric.value}${metric.suffix}`}
              >
                {`${metric.value}${metric.suffix}`}
              </div>
              <div className="home-metrics__label">{metric.label}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
